<!-- src/views/NewsView.vue -->
<template>
  <div class="max-w-5xl mx-auto p-4 space-y-6">
    <!-- Header + Filters -->
    <header class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h1 class="text-2xl font-semibold">Новости</h1>

      <div class="flex flex-wrap items-center gap-3">
        <label class="flex items-center gap-2">
          <span class="text-sm text-gray-600">Видимость</span>
          <select
            v-model="visibleFilter"
            class="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            <option value="all">Все</option>
            <option value="1">Только видимые</option>
            <option value="0">Только скрытые</option>
          </select>
        </label>

        <label class="flex items-center gap-2">
          <span class="text-sm text-gray-600">На странице</span>
          <select
            v-model.number="perPage"
            class="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </label>

        <button
          class="h-9 px-3 rounded-md border border-gray-300 bg-white text-sm hover:bg-gray-50 disabled:opacity-60"
          :disabled="loading"
          @click="load"
        >
          Обновить
        </button>
      </div>
    </header>

    <!-- Create -->
    <section class="rounded-xl border border-gray-200 p-4">
      <h3 class="mb-3 text-base font-medium">Добавить новость</h3>

      <textarea
        v-model.trim="createForm.body"
        rows="3"
        placeholder="Текст новости"
        class="w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
      />
      <label class="mt-3 inline-flex items-center gap-2">
        <input type="checkbox" v-model="createForm.visible" class="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-400" />
        <span class="text-sm text-gray-700">Показывать сразу</span>
      </label>

      <div class="mt-3 flex gap-2">
        <button
          class="inline-flex items-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
          :disabled="creating || !createForm.body"
          @click="create"
        >
          {{ creating ? 'Сохранение…' : 'Сохранить' }}
        </button>
        <button
          class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50"
          :disabled="creating"
          @click="resetCreate"
        >
          Сбросить
        </button>
      </div>
    </section>

    <!-- List -->
    <section>
      <div class="overflow-x-auto rounded-xl border border-gray-200">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 w-20">ID</th>
              <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Текст</th>
              <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 w-44">Создано</th>
              <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 w-32">Видимо</th>
              <th class="py-3 pl-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 w-60">Действия</th>
            </tr>
          </thead>

          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-if="loading">
              <td class="px-3 py-4 text-sm text-gray-600" colspan="5">Загрузка…</td>
            </tr>

            <tr v-for="item in items" :key="item.id">
              <td class="py-4 pl-4 pr-3 text-sm text-gray-900">{{ item.id }}</td>

              <td class="px-3 py-4 align-top">
                <div v-if="editId !== item.id" class="whitespace-pre-wrap text-sm text-gray-900">
                  {{ item.body }}
                </div>
                <div v-else>
                  <textarea
                    v-model.trim="editBody"
                    rows="3"
                    class="w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
              </td>

              <td class="px-3 py-4 text-sm text-gray-700">
                {{ fmt(item.created_at) }}
              </td>

              <td class="px-3 py-4">
                <!-- Toggle без CSS, только Tailwind -->
                <button
                  type="button"
                  :aria-pressed="item.visible"
                  @click="toggleVisibility(item)"
                  :disabled="togglingId === item.id"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition
                         focus:outline-none focus:ring-2 focus:ring-sky-400"
                  :class="item.visible ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
                    :class="item.visible ? 'translate-x-6' : 'translate-x-1'"
                  />
                </button>
              </td>

              <td class="py-4 pl-3 pr-4">
                <div class="flex flex-wrap gap-2">
                  <button
                    v-if="editId !== item.id"
                    class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                    @click="startEdit(item)"
                  >
                    Редактировать
                  </button>

                  <template v-else>
                    <button
                      class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
                      :disabled="savingEdit"
                      @click="cancelEdit"
                    >
                      Отмена
                    </button>
                    <button
                      class="rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
                      :disabled="savingEdit || !editBody"
                      @click="saveEdit(item)"
                    >
                      {{ savingEdit ? 'Сохранение…' : 'Сохранить' }}
                    </button>
                  </template>

                  <button
                    class="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-60"
                    :disabled="deletingId === item.id"
                    @click="remove(item)"
                  >
                    Удалить
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!loading && items.length === 0">
              <td class="px-3 py-6 text-sm text-gray-500" colspan="5">Нет данных</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pager -->
      <div v-if="meta.total > 0" class="mt-4 flex items-center justify-center gap-3">
        <button
          class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
          :disabled="page <= 1 || loading"
          @click="goto(page - 1)"
        >
          « Назад
        </button>
        <span class="text-sm text-gray-700">Стр. {{ page }} из {{ meta.last_page }}</span>
        <button
          class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
          :disabled="page >= meta.last_page || loading"
          @click="goto(page + 1)"
        >
          Вперёд »
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { fetchNews, createNews, updateNews, setNewsVisibility, deleteNews } from '@/services/newsService';

// table state
const items = ref([]);
const loading = ref(false);
const page = ref(1);
const perPage = ref(20);
const visibleFilter = ref('all'); // 'all' | '1' | '0'
const meta = reactive({ total: 0, last_page: 1 });

async function load() {
  loading.value = true;
  try {
    const params = { page: page.value, limit: perPage.value };
    if (visibleFilter.value !== 'all') params.visible = Number(visibleFilter.value);
    const res = await fetchNews(params);
    const p = res.data;
    items.value = p.data ?? [];
    meta.total = p.total ?? items.value.length;
    meta.last_page = p.last_page ?? 1;
  } finally {
    loading.value = false;
  }
}

// create
const createForm = reactive({ body: '', visible: true });
const creating = ref(false);
function resetCreate() { createForm.body = ''; createForm.visible = true; }
async function create() {
  creating.value = true;
  try {
    await createNews(createForm.body, createForm.visible);
    resetCreate();
    page.value = 1;
    await load();
  } finally {
    creating.value = false;
  }
}

// toggle visibility (кнопка с Tailwind)
const togglingId = ref(null);
async function toggleVisibility(item) {
  const nextVal = !item.visible;
  togglingId.value = item.id;
  try {
    await setNewsVisibility(item.id, nextVal); // на бэке PATCH или POST+_method
    item.visible = nextVal; // optimistic
  } catch (e) {
    // оставляем как было, логуем
    console.error(e);
  } finally {
    togglingId.value = null;
  }
}

// edit
const editId = ref(null);
const editBody = ref('');
const savingEdit = ref(false);

function startEdit(item) { editId.value = item.id; editBody.value = item.body; }
function cancelEdit() { editId.value = null; editBody.value = ''; }

async function saveEdit(item) {
  if (!editBody.value) return;
  savingEdit.value = true;
  try {
    const res = await updateNews(item.id, { body: editBody.value });
    item.body = res.data?.body ?? editBody.value;
    cancelEdit();
  } finally {
    savingEdit.value = false;
  }
}

// delete
const deletingId = ref(null);
async function remove(item) {
  if (!confirm(`Удалить новость #${item.id}?`)) return;
  deletingId.value = item.id;
  try {
    await deleteNews(item.id);
    if (items.value.length === 1 && page.value > 1) page.value -= 1;
    await load();
  } finally {
    deletingId.value = null;
  }
}

// pager
async function goto(p) { page.value = Math.max(1, Math.min(p, meta.last_page)); await load(); }

// helpers
function fmt(dt) { try { return new Date(dt).toLocaleString(); } catch { return dt ?? ''; } }

watch([perPage, visibleFilter], async () => { page.value = 1; await load(); });
onMounted(load);
</script>
